import { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowLeft, Download, AlertTriangle } from 'lucide-react';

const TERMINAL_STATUSES = ['paid', 'rejected', 'cancelled'];

export default function PaymentResult() {
  const { API_URL, refetchMembership } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [txn, setTxn] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const gateway = searchParams.get('gateway');
  const txnId = searchParams.get('txn_id');

  useEffect(() => {
    if (!gateway || !txnId) {
      navigate('/dashboard');
      return;
    }
    checkStatus();
  }, []);

  useEffect(() => {
    if (status === 'processing' && pollCount < 10) {
      const timer = setTimeout(() => {
        checkStatus();
        setPollCount(prev => prev + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, pollCount]);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const res = await axios.get(`${API_URL}/payments/${gateway}/status/${txnId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data.status);
      setTxn(res.data.transaction);
      // The membership context is only fetched once per session (see App.js), so once
      // this payment reaches a final state we need to explicitly refresh it — otherwise
      // the dashboard could keep showing the pre-payment membership status.
      if (TERMINAL_STATUSES.includes(res.data.status)) {
        refetchMembership(true);
      }
    } catch {
      setStatus('error');
    }
  };

  const formatCurrency = (amount, currency = 'COP') => {
    const formatted = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
    return `$${formatted} ${currency}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-CO', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handleDownloadReceipt = () => {
    if (!txn) return;
    const actionLabels = { payment: 'Pago', renewal: 'Renovación', upgrade: 'Cambio de Plan' };
    const receipt = `
══════════════════════════════════════
       COMPROBANTE DE PAGO - KONEMED
══════════════════════════════════════

Transacción:    ${txn.id || 'N/A'}
Fecha:          ${formatDate(txn.updated_at || txn.created_at)}
Estado:         ${status === 'paid' ? 'APROBADO' : 'RECHAZADO'}

──────────────────────────────────────
Concepto:       ${txn.description || 'N/A'}
Tipo:           ${actionLabels[txn.action_type] || 'Pago'}
Pasarela:       ${gateway === 'bold' ? 'Bold.co' : 'Flow.cl'}
Monto:          ${formatCurrency(txn.amount, txn.currency)}
──────────────────────────────────────

Ref. Pasarela:  ${txn.gateway_order_id || 'N/A'}

══════════════════════════════════════
         www.konemed.com
══════════════════════════════════════
    `.trim();

    const blob = new Blob([receipt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comprobante_konemed_${txn.id?.slice(0, 8) || 'pago'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusConfig = {
    paid: {
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      title: 'Pago Realizado',
      subtitle: 'Tu pago ha sido procesado exitosamente.'
    },
    rejected: {
      icon: XCircle,
      color: 'text-red-500',
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'Pago Rechazado',
      subtitle: 'El pago no pudo ser procesado. Intenta nuevamente.'
    },
    cancelled: {
      icon: XCircle,
      color: 'text-slate-500',
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      title: 'Pago Cancelado',
      subtitle: 'El proceso de pago fue cancelado.'
    },
    processing: {
      icon: Loader2,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'Procesando Pago',
      subtitle: 'Tu pago está siendo verificado. Esto puede tomar unos segundos...'
    },
    loading: {
      icon: Loader2,
      color: 'text-slate-400',
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      title: 'Consultando estado...',
      subtitle: ''
    },
    error: {
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      title: 'Error de consulta',
      subtitle: 'No se pudo verificar el estado del pago. Verifica en tu dashboard.'
    }
  };

  const cfg = statusConfig[status] || statusConfig.error;
  const Icon = cfg.icon;
  const isSpinning = status === 'processing' || status === 'loading';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-result-page">
      <div className="w-full max-w-lg">
        <div className={`bg-white rounded-2xl border ${cfg.border} shadow-sm overflow-hidden`}>
          <div className={`${cfg.bg} px-8 py-10 text-center`}>
            <Icon size={64} className={`${cfg.color} mx-auto mb-4 ${isSpinning ? 'animate-spin' : ''}`} />
            <h1 className="text-2xl font-bold text-slate-900" data-testid="payment-status-title">{cfg.title}</h1>
            <p className="text-slate-600 mt-2">{cfg.subtitle}</p>
          </div>

          {txn && (
            <div className="px-8 py-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Concepto</span>
                <span className="text-sm font-semibold text-slate-900 text-right max-w-[60%]">{txn.description}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Monto</span>
                <span className="text-lg font-bold text-slate-900">{formatCurrency(txn.amount, txn.currency)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Pasarela</span>
                <span className="text-sm font-semibold text-slate-700">{gateway === 'bold' ? 'Bold.co' : 'Flow.cl'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm text-slate-500">Referencia</span>
                <span className="text-xs font-mono text-slate-500">{txn.gateway_order_id || txn.id?.slice(0, 12)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-slate-500">Fecha</span>
                <span className="text-sm text-slate-700">{formatDate(txn.created_at)}</span>
              </div>
            </div>
          )}

          <div className="px-8 pb-8 pt-2 space-y-3">
            {status === 'paid' && (
              <button
                onClick={handleDownloadReceipt}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors"
                data-testid="download-receipt-btn"
              >
                <Download size={20} />
                Descargar Comprobante
              </button>
            )}
            <button
              onClick={() => { refetchMembership(true); navigate('/dashboard'); }}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-xl transition-colors"
              data-testid="go-to-dashboard-btn"
            >
              <ArrowLeft size={20} />
              Volver al Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
